/**
 * Mount Clip Editor
 *
 * Bridges the React ClipEditor overlay into the vanilla DOM world of index.ts.
 * Renders into the #clip-editor-container element when explicitly opened
 * via the Animation behavior's "Create Clip" button.
 *
 * Observes inspector pane widths to position the overlay between sidebars.
 */

import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ClipEditor } from './behaviors/clip-editor';
import type { SceneObject } from 'trace-viewer/scene';
import type { Scene } from '@babylonjs/core/scene';
import type { ResourceManager } from 'trace-viewer/scene';
import type { TransformKeyframe, ClipData } from 'trace-viewer/animation';
import { generateClipsGLB, extractKeyframesFromAnimationGroup, clearClipNameCache, loadAssetContainerFromSrc } from 'trace-viewer/animation';
import { Animation } from 'trace-viewer/behaviors';
import { dispatchBehaviorChanged } from './editor-events';
import { observeInspectorPaneInsets } from './inspector-panes';

export interface ClipEditorOpenOptions {
    initialKeyframes?: TransformKeyframe[];
    initialClipName?: string;
    existingClipNames?: string[];
}

export interface ClipEditorControls {
    /** Open the clip editor for the given scene object */
    open: (sceneObject: SceneObject, scene: Scene, resources: ResourceManager, options?: ClipEditorOpenOptions) => void;
    /** Trigger a "set keyframe" action (for S shortcut) */
    setKey: () => void;
    /** Whether the clip editor is currently visible */
    isActive: () => boolean;
    /** Close the editor */
    close: () => void;
    /** Clean up */
    dispose: () => void;
}

export function mountClipEditor(): ClipEditorControls {
    const containerEl = document.getElementById('clip-editor-container');
    if (!containerEl) {
        throw new Error('Missing #clip-editor-container element');
    }
    const container: HTMLElement = containerEl;

    let root: Root | null = null;
    let active = false;
    let renderKey = 0;
    let disposePaneObserver: (() => void) | null = null;
    const setKeyRef: React.MutableRefObject<(() => void) | null> = { current: null };

    // Track current context for save callback
    let currentResources: ResourceManager | null = null;
    let currentSceneObject: SceneObject | null = null;

    function setupPaneObserver() {
        disposePaneObserver?.();
        disposePaneObserver = observeInspectorPaneInsets(({ left, right }) => {
            container.style.left = `${left}px`;
            container.style.right = `${right}px`;
        });
    }

    function closeEditor() {
        active = false;
        root?.render(null);
    }

    function render(sceneObject: SceneObject, scene: Scene, resources: ResourceManager, options?: ClipEditorOpenOptions) {
        active = true;
        currentResources = resources;
        currentSceneObject = sceneObject;
        root ??= createRoot(container);

        // Stop any active playback so the old clip doesn't interfere with
        // the new clip editor session. clearPlayback() fully resets the
        // behavior so getPlaybackState() returns null.
        const anim = sceneObject.getBehavior(Animation);
        if (anim) {
            anim.clearPlayback();
        }

        requestAnimationFrame(() => setupPaneObserver());

        // Increment key to force React to remount ClipEditor with fresh state
        renderKey++;

        root.render(
            React.createElement(ClipEditor, {
                key: renderKey,
                sceneObject,
                scene,
                onSetKeyRef: setKeyRef,
                initialKeyframes: options?.initialKeyframes,
                initialClipName: options?.initialClipName,
                existingClipNames: options?.existingClipNames,
                getExternalPlayback: () => {
                    if (!currentSceneObject) return null;
                    const anim = currentSceneObject.getBehavior(Animation);
                    return anim?.getPlaybackState() ?? null;
                },
                onPlay: (clipName: string) => {
                    if (!currentSceneObject) return;
                    const anim = currentSceneObject.getBehavior(Animation);
                    void anim?.play({ clip: clipName });
                },
                onPause: () => {
                    if (!currentSceneObject) return;
                    const anim = currentSceneObject.getBehavior(Animation);
                    anim?.pause();
                },
                onStop: () => {
                    if (!currentSceneObject) return;
                    const anim = currentSceneObject.getBehavior(Animation);
                    anim?.stop();
                },
                onSeek: (time: number) => {
                    if (!currentSceneObject) return;
                    const anim = currentSceneObject.getBehavior(Animation);
                    anim?.seek(time);
                },
                onSave: async (keyframes: TransformKeyframe[], clipName: string, fps: number) => {
                    if (!currentResources || !currentSceneObject) return;
                    const anim = currentSceneObject.getBehavior(Animation);
                    if (!anim) return;

                    // Build the list of all clips for this GLB.
                    // Load sibling clips from the existing GLB file (if any),
                    // then add/replace the clip being edited.
                    const allClips: ClipData[] = [];

                    const existingSrc = anim.getData()?.src;
                    if (existingSrc) {
                        try {
                            const container = await loadAssetContainerFromSrc(
                                existingSrc, scene, currentResources,
                            );
                            for (const group of container.animationGroups) {
                                if (group.name === clipName) continue; // replaced below
                                allClips.push({
                                    name: group.name,
                                    keyframes: extractKeyframesFromAnimationGroup(group),
                                    fps,
                                });
                            }
                            container.dispose();
                        } catch {
                            // No existing GLB yet — first save
                        }
                    }

                    allClips.push({ name: clipName, keyframes, fps });

                    // Generate combined multi-clip GLB
                    const blob = await generateClipsGLB(allClips, scene);

                    // Derive asset path from the object name (stable across clips)
                    const objectName = currentSceneObject.name.toLowerCase().replace(/\s+/g, '_');
                    const assetPath = `assets/clips/${objectName}.glb`;
                    // Remove old entry so addAssetWithDedup doesn't deduplicate against stale content
                    currentResources.removeAsset(assetPath);
                    const actualPath = await currentResources.addAssetWithDedup(assetPath, blob);

                    // Invalidate cached clip names since the GLB content changed
                    clearClipNameCache();

                    const src = `./${actualPath}`;
                    const errors = await anim.commit({ src, clip: clipName, target: '@self' });
                    if (errors.length) {
                        console.error('[ClipEditor] Commit errors:', errors);
                    }
                    dispatchBehaviorChanged({
                        objectPath: currentSceneObject.getPath(),
                        behaviorType: 'Animation',
                        action: 'updated',
                    });
                },
                onClose: closeEditor,
            })
        );
    }

    return {
        open: render,
        setKey: () => {
            if (active && setKeyRef.current) {
                setKeyRef.current();
            }
        },
        isActive: () => active,
        close: closeEditor,
        dispose: () => {
            disposePaneObserver?.();
            disposePaneObserver = null;
            root?.unmount();
            root = null;
            active = false;
            currentResources = null;
            currentSceneObject = null;
        },
    };
}
